import type {
  IPlaybackController,
  PlaybackState,
  PlaybackOptions,
  IEventBus,
  PreviewHandle,
  TimeUs,
} from './types';
import { MeframeEvent } from '../event/events';
import { quantizeTimestampToFrame } from '../utils/time-utils';
import type { GlobalAudioSession } from '../stages/compose/GlobalAudioSession';
import type { Orchestrator } from '../orchestrator';
import { WaiterReplacedError } from '../utils/errors';

/**
 * Playback controller for preview
 * Internal implementation - not exposed directly to external consumers
 */
export class PlaybackController implements IPlaybackController, PreviewHandle {
  private orchestrator: Orchestrator;
  private eventBus: IEventBus;
  private canvas: HTMLCanvasElement | OffscreenCanvas;
  private ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

  // Playback state
  currentTimeUs: TimeUs = 0;
  private state: PlaybackState = 'idle';
  private playbackRate = 1.0;
  private volume = 1.0;
  private loop = false;

  // Animation loop
  private rafId: number | null = null;
  private startTime = 0;

  // Frame tracking
  private frameCount = 0;
  private lastFrameTime = 0;
  private fps = 0;
  private audioContext: AudioContext | null = null;
  private audioSession: GlobalAudioSession | null = null;

  // Buffering state
  private isBuffering = false;
  private currentSeekId = 0;
  private wasPlayingBeforeSeek = false;

  constructor(orchestrator: Orchestrator, eventBus: IEventBus, options: PlaybackOptions) {
    this.orchestrator = orchestrator;
    this.eventBus = eventBus;
    this.canvas = options.canvas;

    // Get 2D context with high quality settings
    const ctx = this.canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      colorSpace: 'srgb',
    } as any);
    if (!ctx) {
      throw new Error('Failed to get 2D context from canvas');
    }
    this.ctx = ctx as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

    // Configure high quality rendering
    this.ctx.imageSmoothingEnabled = true;
    this.ctx.imageSmoothingQuality = 'high';

    // Set initial time if provided
    if (options.startUs !== undefined) {
      this.currentTimeUs = options.startUs;
    }

    if (options.rate !== undefined) {
      this.playbackRate = options.rate;
    }

    if (options.loop !== undefined) {
      this.loop = options.loop;
    }

    if (options.autoStart) {
      this.play();
    }

    this.setupListeners();
  }

  // Playback control
  play(): void {
    if (this.state === 'playing') return;

    this.wasPlayingBeforeSeek = true; // User wants to play
    void this.startPlayback();
  }

  private async startPlayback(): Promise<void> {
    const wasIdle = this.state === 'idle';
    const seekId = this.currentSeekId;

    try {
      // Render first frame (may trigger buffering if cache miss)
      await this.renderCurrentFrame(this.currentTimeUs, true);

      // Check if seek happened during render
      if (seekId !== this.currentSeekId) {
        return;
      }

      this.state = 'playing';
      this.startTime = performance.now() - this.currentTimeUs / 1000 / this.playbackRate;
      await this.ensureAudioContext();
      if (this.audioSession && this.audioContext) {
        await this.audioSession.startPlayback(this.currentTimeUs, this.audioContext);
      }
      this.playbackLoop();

      this.eventBus.emit(MeframeEvent.PlaybackPlay);
    } catch (error) {
      console.error('[PlaybackController] Failed to start playback:', error);
      this.state = wasIdle ? 'idle' : 'paused';
      this.eventBus.emit(MeframeEvent.PlaybackError, error as Error);
    }
  }

  pause(): void {
    if (this.state !== 'playing') return;

    this.state = 'paused';
    this.wasPlayingBeforeSeek = false; // User explicitly paused

    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }

    this.audioSession?.stopPlayback();

    this.eventBus.emit(MeframeEvent.PlaybackPause);
  }

  stop(): void {
    this.pause();
    this.currentTimeUs = 0;
    this.state = 'idle';
    this.wasPlayingBeforeSeek = false; // Reset seek state
    this.frameCount = 0; // Reset frame counter
    this.lastFrameTime = 0; // Reset frame timing
    this.fps = 0; // Reset FPS

    // Clear canvas
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    this.audioSession?.reset();

    this.eventBus.emit(MeframeEvent.PlaybackStop);
  }

  async seek(timeUs: TimeUs): Promise<void> {
    const previousState = this.state;

    // Stop playback without changing wasPlayingBeforeSeek
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    this.audioSession?.stopPlayback();

    const clamped = this.clampTime(timeUs);
    this.currentTimeUs = clamped;
    this.currentSeekId++; // Invalidate previous seek operations
    this.isBuffering = false; // Reset buffering flag

    this.state = 'seeking';

    const seekId = this.currentSeekId;

    try {
      await this.renderCurrentFrame(clamped, false);

      // Check if another seek happened during render
      if (seekId !== this.currentSeekId) {
        return;
      }

      this.eventBus.emit(MeframeEvent.PlaybackSeek, { timeUs: this.currentTimeUs });

      if (this.wasPlayingBeforeSeek) {
        await this.startPlayback();
      } else {
        this.state = previousState === 'idle' ? 'idle' : 'paused';
      }
    } catch (error) {
      // Check if this seek is still current
      if (seekId !== this.currentSeekId) {
        return;
      }
      console.error('[PlaybackController] Seek error:', error);
      this.eventBus.emit(MeframeEvent.PlaybackError, error as Error);
      this.state = previousState === 'idle' ? 'idle' : 'paused';
    }
  }

  // Playback properties
  setRate(rate: number): void {
    // Adjust start time to maintain current position
    const elapsed = performance.now() - this.startTime;
    this.playbackRate = rate;
    this.startTime = performance.now() - elapsed / rate;

    this.eventBus.emit(MeframeEvent.PlaybackRateChange, { rate });

    this.audioSession?.setPlaybackRate(this.playbackRate);
  }

  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    this.eventBus.emit(MeframeEvent.PlaybackVolumeChange, { volume: this.volume });

    this.audioSession?.setVolume(this.volume);
  }

  setMute(muted: boolean): void {
    if (muted) {
      this.audioSession?.stopPlayback();
    } else if (this.state === 'playing' && this.audioContext) {
      this.audioSession?.startPlayback(this.currentTimeUs, this.audioContext);
    }
  }

  setLoop(loop: boolean): void {
    this.loop = loop;
  }

  get duration(): TimeUs {
    const modelDuration = this.orchestrator.compositionModel?.durationUs;
    if (modelDuration !== undefined) {
      return modelDuration;
    }

    return 0;
  }

  get isPlaying(): boolean {
    return this.state === 'playing';
  }

  setAudioSession(session: GlobalAudioSession): void {
    this.audioSession = session;
  }

  // Resume is just an alias for play
  resume(): void {
    this.play();
  }

  on(event: string, handler: (payload: any) => void): void {
    this.eventBus.on(event as MeframeEvent, handler);
  }

  off(event: string, handler: (payload: any) => void): void {
    this.eventBus.off(event as MeframeEvent, handler);
  }

  private setupListeners(): void {
    this.orchestrator.on(MeframeEvent.CacheCover, (event) => {
      // Only render cover in idle/paused state, not during playback
      if (this.state === 'playing' || this.state === 'buffering') {
        return;
      }
      // CacheCover event now contains global timeUs
      this.renderCurrentFrame(event.timeUs);
    });
  }

  // Private methods
  private playbackLoop(): void {
    // Only continue loop if actively playing (not buffering/paused/etc)
    if (this.state !== 'playing') {
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
      return;
    }

    this.rafId = requestAnimationFrame(async () => {
      // Check state again after async boundary
      if (this.state !== 'playing') {
        return;
      }

      this.updateTime();

      // Update audio clips based on current time
      this.audioSession?.updateTime(this.currentTimeUs);

      await this.renderCurrentFrame(this.currentTimeUs, true);

      // Check if still playing after render (might have entered buffering)
      if (this.state !== 'playing') {
        return;
      }

      // Calculate FPS based on actual frame timing
      const now = performance.now();
      if (this.lastFrameTime > 0) {
        const deltaTime = now - this.lastFrameTime;
        const instantFps = 1000 / deltaTime;
        this.fps = this.fps > 0 ? this.fps * 0.9 + instantFps * 0.1 : instantFps;
      }
      this.lastFrameTime = now;

      this.frameCount++;

      this.playbackLoop();
    });
  }

  private updateTime(): void {
    const elapsed = (performance.now() - this.startTime) * this.playbackRate;
    const rawTimeUs = elapsed * 1000;
    const fps = this.orchestrator.compositionModel?.fps;
    this.currentTimeUs = quantizeTimestampToFrame(rawTimeUs, 0, fps, 'nearest');

    // Check if reached end
    if (this.currentTimeUs >= this.duration) {
      if (this.loop) {
        this.currentTimeUs = 0;
        this.startTime = performance.now();
      } else {
        this.currentTimeUs = this.duration;
        this.pause();
        this.state = 'ended';
        this.eventBus.emit(MeframeEvent.PlaybackEnded, { timeUs: this.currentTimeUs });
      }
    }

    // Emit time update
    this.eventBus.emit(MeframeEvent.PlaybackTimeUpdate, { timeUs: this.currentTimeUs });
  }

  async renderCurrentFrame(timeUs: TimeUs, immediate = true): Promise<void> {
    try {
      const rcFrame = await this.orchestrator.renderFrame(timeUs, { immediate });
      if (!rcFrame) {
        // Cache miss during playback - trigger buffering
        if (this.state === 'playing') {
          await this.handlePlaybackBuffering(timeUs);
        }
        return;
      }
      await rcFrame.use((frame) => {
        // Ensure high quality rendering for every frame
        this.ctx.imageSmoothingEnabled = true;
        this.ctx.imageSmoothingQuality = 'high';
        this.ctx.drawImage(frame, 0, 0, this.canvas.width, this.canvas.height);
      });
    } catch (error) {
      console.error('Render error:', error);
      this.eventBus.emit(MeframeEvent.PlaybackError, error as Error);
    }
  }

  private async handlePlaybackBuffering(timeUs: TimeUs): Promise<void> {
    if (this.isBuffering) {
      return;
    }

    const wasPlaying = this.state === 'playing';
    if (!wasPlaying) return;

    const seekId = this.currentSeekId;
    this.isBuffering = true;
    this.state = 'buffering';
    this.eventBus.emit(MeframeEvent.PlaybackBuffering);

    try {
      // await this.orchestrator.ensureClipCache(timeUs);

      const ready = await this.orchestrator.waitForClipReady(timeUs, {
        minFrameCount: 3, // 等待 3 帧，确保连续播放不会立即再次 miss
        timeoutMs: 5_000,
      });

      // Check if seek happened during buffering
      if (seekId !== this.currentSeekId) {
        return;
      }

      if (!ready) {
        console.warn('[PlaybackController] Buffering timeout during playback', timeUs);
      }

      this.state = 'playing';
      this.startTime = performance.now() - timeUs / 1000 / this.playbackRate;
      this.eventBus.emit(MeframeEvent.PlaybackPlay);

      if (!this.rafId) {
        this.playbackLoop();
      }
    } catch (error) {
      // Ignore WaiterReplacedError (happens during fast seeks)
      if (error instanceof WaiterReplacedError) {
        return;
      }
      // Check if seek happened during error handling
      if (seekId !== this.currentSeekId) {
        return;
      }
      console.error('[PlaybackController] Buffering error:', error);
      this.state = 'paused';
      this.eventBus.emit(MeframeEvent.PlaybackError, error as Error);
    } finally {
      this.isBuffering = false;
    }
  }

  private clampTime(timeUs: TimeUs): TimeUs {
    return Math.max(0, Math.min(timeUs, this.duration));
  }

  // Cleanup
  dispose(): void {
    this.stop();
  }

  private async ensureAudioContext(): Promise<void> {
    if (this.audioContext) {
      return;
    }

    this.audioContext = new AudioContext();
  }
}
